import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, HostBinding, Input, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { UserLocationResponse } from '@core-mkt/interfaces/geolocation';
import { GeolocationService } from '@core-mkt/services/geolocation/geolocation.service';
import { map } from 'rxjs';
import type { ProductPicker } from '../../layout/layout/layout';
import { VisionButton } from '../../vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '../../vision-button/vision-button/vision-button.component';
import { VisionSelectComponent } from '../../vision-select/vision-select/vision-select.component';

@Component({
  selector: 'product-picker',
  templateUrl: './product-picker.component.html',
  standalone: true,
  imports: [VisionButtonComponent, VisionSelectComponent],
})
export class ProductPickerComponent implements OnInit, OnChanges {
  @Input() data: ProductPicker[]; // Data from the parent component
  @Input() ctaButtonData: VisionButton; // If data from parent component needs to override CTA button data
  states: { value: string; label: string }[] = []; // States to show on the select field
  selectedProducts: {
    value: string;
    label: string;
    href: string;
    ctaOverride: string;
    noFollow: boolean;
    noReferrer: boolean;
  }[] = []; // Products to show on the select field
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  stateFromGeolocation: string; // State from geolocation
  productDefault: string; // product default value
  selectedProductsDisabled = true; // Disable the select field if no products are selected
  stateAbbrFromCookie: string; // State abbreviation from cookie
  isLoadingState: boolean; // Loading state for geolocation
  selectedState: string; // Currently selected state
  courseTypes: { value: string; label: string }[] = []; // Course types for the selected state (INS only)
  showCourseType = false; // Render the Course Type dropdown when the data carries courseType (INS finder)
  courseTypeDisabled = true; // Disable the Course Type select until a state is selected (mirrors Product)
  selectedCourseType: string | null = null; // Currently selected course type
  isTwoStepFinder = false; // Every state + course type resolves to one row, so the finder is State + Course Type only

  defaultCtaButtonData: VisionButton = {
    bgTheme: 'bg-white',
    buttonText: 'Get My License',
    buttonLink: '',
    fullWidth: true,
    nofollow: false,
    noreferrer: false,
    noopener: false,
    newTab: false,
    useIcon: true,
  };

  finalCtaButtonData: VisionButton = {
    bgTheme: 'bg-white',
    buttonText: 'Get My License',
    buttonLink: '',
    fullWidth: true,
    nofollow: false,
    noreferrer: false,
    noopener: false,
    newTab: false,
    useIcon: true,
  };

  constructor(public geolocation: GeolocationService) {
    this.geolocation?.isLoading.subscribe((isLoading) => {
      this.isLoadingState = isLoading;
    });
  }

  /**
   * Marks the host with `course-type-finder` only when the picker's data carries
   * courseType rows (the INS course finder). Every other brand leaves courseType
   * empty, so the class is absent and their product-picker keeps its plain styling.
   * This is the scope key for the finder-only card styling in
   * styles/_product-finder.scss — brand-agnostic and precise (7 of 9 brands are
   * ace mode, so scoping by body.ace-mode would wrongly restyle them all).
   */
  @HostBinding('class.course-type-finder') get isCourseFinder(): boolean {
    return !!this.data?.[0]?.productPickerTable?.some((row) => !!row.courseType);
  }

  get finderHeading(): string {
    // Optional card heading authored on the Craft category (INS finder only).
    return this.data?.[0]?.finderHeading || '';
  }

  /**
   * Disables the CTA while the finder cascade is unresolved (no destination yet).
   * Binding vision-button's `[disabled]` input — not just CSS — is what blocks
   * BOTH mouse and keyboard activation (its `onClick` guard `preventDefault`s the
   * empty-href jump). Scoped to the finder (`showCourseType`) so non-INS pickers,
   * whose CTA always resolves to the first product, are never disabled — unchanged
   * from before. The muted-pink look is applied in _product-finder.scss.
   */
  get ctaDisabled(): boolean {
    return this.showCourseType && !this.finalCtaButtonData?.buttonLink;
  }

  /**
   * Hides the Product select in the finder when the current selection resolves to a
   * single destination (Continuing Education routes to one comparison page with no
   * product-level distinction; a single-product Pre-License state is the same case).
   * That single row is auto-selected in setProductsForSelection, so there is no
   * choice to offer — rendering a one-option (and, for CE, possibly blank-labelled)
   * dropdown would be pointless. The cleared initial state (length === 0) keeps
   * showing the disabled placeholder, unless the whole category is a two-step finder
   * (isTwoStepFinder), where the Product select never has a choice to offer and is
   * hidden from first render.
   */
  get hideProductSelect(): boolean {
    return this.showCourseType && (this.isTwoStepFinder || this.selectedProducts.length === 1);
  }

  /**
   * The default CTA label. Sourced from the Craft category field `productPickerCtaLabel`
   * (category-scoped, like finderHeading), then the SideProductPicker block ctaLabel
   * (threaded in as ctaButtonData.buttonText), then 'Get My License'. Used as the initial
   * label and whenever a resolved product has no per-product ctaOverride (EPS-4593 AC2).
   */
  get blockDefaultLabel(): string {
    return this.data?.[0]?.productPickerCtaLabel?.trim() || this.ctaButtonData?.buttonText?.trim() || 'Get My License';
  }

  get stateDropdownLabel(): string {
    return this.data?.[0]?.stateDropdownLabel || 'Select a State';
  }

  get productDropdownLabel(): string {
    return this.data?.[0]?.productDropdownLabel || 'Select a Product';
  }

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && changes['data'].currentValue) {
      this.initializeComponent();
    }
  }

  /**
   * Initializes the component with data
   */
  private initializeComponent(): void {
    // Only proceed if we have data
    if (!this.data || !Array.isArray(this.data) || this.data.length === 0) {
      return;
    }

    // Set the states for the product picker
    this.setStates();

    // Render the Course Type level from the start (disabled, placeholder) whenever
    // the data carries courseType rows (the INS finder), mirroring the Product
    // dropdown. Brands without courseType never render it.
    this.showCourseType = !!this.data?.[0]?.productPickerTable?.some((row) => !!row.courseType);
    this.isTwoStepFinder = this.showCourseType && this.hasOneRowPerStateAndCourseType();

    // Get the user location process - browser only (cookies and geolocation APIs unavailable on SSR)
    if (this.isBrowser) {
      if (!this.geolocation.getLocationCookie()) {
        // If we don't have a cookie, get the user location
        this.getUserLocation();
      } else {
        // If we have a cookie, get the user location from the cookie
        this.getUserLocationFromCookie();
      }
    }
    // Only set the initial CTA data if it hasn't been set yet
    if (!this.finalCtaButtonData || !this.finalCtaButtonData.buttonLink) {
      // Use the provided CTA data if available, otherwise use default button data above,
      // then seed the label from the configured default (Craft productPickerCtaLabel) so it
      // shows before any product selection resolves one (EPS-4593 AC2).
      this.finalCtaButtonData = {
        ...(this.ctaButtonData || this.defaultCtaButtonData),
        buttonText: this.blockDefaultLabel,
      };
    }
  }

  /**
   * Retrieves the user's location from the cookie.
   * Sets the state abbreviation from the cookie and gets the corresponding state name from geolocation.
   * If the state name is available, it calls the handleStateChange method with the state name.
   * Finally, it sets the isLoadingState flag to false.
   */
  getUserLocationFromCookie(): void {
    this.stateAbbrFromCookie = this.geolocation.getLocationCookie();
    this.stateFromGeolocation = this.geolocation.getStateName(this.stateAbbrFromCookie);
    this.productDefault = this.selectedProducts[0]?.value || '';

    if (this.stateFromGeolocation) {
      this.handleStateChange(this.stateFromGeolocation);
    }

    this.geolocation.setIsLoadingState(false);
  }

  /**
   * Retrieves the user's location and performs necessary actions based on the location.
   */
  getUserLocation(): void {
    this.geolocation.setIsLoadingState(true);

    this.geolocation
      .getUserLocation()
      .pipe(
        map((location: UserLocationResponse) => {
          return location.stateabbr;
        }),
      )
      .subscribe((state) => {
        this.geolocation.setLocationCookie(state);
        this.geolocation.setStateAbbr(state);

        this.stateFromGeolocation = this.geolocation.getStateName(state);
        this.productDefault = this.selectedProducts[0]?.value || '';
        // If we have a state name, call the handleStateChange method
        if (this.stateFromGeolocation) {
          this.handleStateChange(this.stateFromGeolocation);
        }
        this.geolocation.setIsLoadingState(false);
      });
  }

  /**
   * Sets the states for the product picker.
   */
  setStates() {
    if (this.data?.[0]?.productPickerTable !== null && this.data?.[0]?.productPickerTable !== undefined) {
      this.data?.[0]?.productPickerTable.forEach((product) => {
        if (product && !this.states.some((state) => state.value === product.state)) {
          this.states.push({ value: product.state, label: product.state });
        }
      });
    }
  }

  /**
   * Handles the state change event and updates the selected products based on the selected state.
   * @param state - The new state value.
   */
  handleStateChange(state: string): void {
    this.selectedState = state;
    // Update the cookie value if user selects a different state
    this.geolocation.updateLocationCookie(this.geolocation.getStateAbbr(state));

    // Course Type is an optional middle level (INS only — it renders from init
    // when the data carries courseType, see initializeComponent).
    this.courseTypes = this.getCourseTypesForState(state);

    if (this.showCourseType) {
      // INS finder: never auto-select. Enable the Course Type select when this
      // state has options (disable it when the state has none — mixed data), and
      // keep the Product select + CTA cleared until the user completes the cascade.
      // Branching on the finder flag (not this state's courseTypes.length) means a
      // finder state that happens to have no course types can't fall through to the
      // auto-select path below.
      this.courseTypeDisabled = this.courseTypes.length === 0;
      this.selectedCourseType = null;
      this.clearProducts();
    } else {
      // Standard 2-level picker (all other brands): auto-select the first product.
      this.courseTypeDisabled = true;
      this.selectedCourseType = null;
      this.setProductsForSelection(state, null, true);
    }
  }

  /**
   * Handles the course type change: re-filters products for the selected state +
   * course type and refreshes the CTA.
   * @param courseType - The selected course type.
   */
  handleCourseTypeChange(courseType: string): void {
    this.selectedCourseType = courseType;
    // Populate the products for state + course type but leave the Product select
    // on its placeholder (no auto-select) so the user makes the final choice.
    this.setProductsForSelection(this.selectedState, courseType, false);
  }

  /**
   * Clears the product list and resets the CTA — used on the finder path while the
   * user still has to choose a course type (and then a product).
   */
  private clearProducts(): void {
    this.selectedProducts = [];
    this.selectedProductsDisabled = true;
    this.productDefault = '';
    this.finalCtaButtonData = { ...this.finalCtaButtonData, buttonLink: '' };
  }

  /**
   * True when every row carries a course type and no state + course type pair has
   * more than one row, i.e. the category lists no products to choose between and
   * every pair routes to one destination (e.g. the course-type comparison page).
   * Checked on first load so the finder renders as State + Course Type from the start.
   * This is a content contract: adding a second row for any pair in the category
   * brings the Product select back for every state. A row without a course type
   * (mixed data) also keeps it, since that state has no Course Type to pick.
   */
  private hasOneRowPerStateAndCourseType(): boolean {
    const rows = this.data?.[0]?.productPickerTable ?? [];
    const pairs = new Set(rows.map((row) => `${row.state}|${row.courseType}`));
    return rows.every((row) => !!row.courseType) && pairs.size === rows.length;
  }

  /**
   * Distinct non-empty course types available for the given state (INS only).
   * @param state - The selected state.
   */
  private getCourseTypesForState(state: string): { value: string; label: string }[] {
    const courseTypes: { value: string; label: string }[] = [];
    this.data?.[0]?.productPickerTable?.forEach((item) => {
      if (item.state === state && item.courseType && !courseTypes.some((c) => c.value === item.courseType)) {
        courseTypes.push({ value: item.courseType, label: item.courseType });
      }
    });
    return courseTypes;
  }

  /**
   * Builds the product list for a state (optionally filtered by course type),
   * toggles the product select, and auto-selects the first product so the CTA
   * always resolves — matching the original picker behavior.
   * @param state - The selected state.
   * @param courseType - The selected course type, or null when there is no course-type level.
   * @param autoSelect - When true, auto-select the first product and resolve the CTA
   *   (standard 2-level picker). When false (INS finder), leave the Product select on
   *   its placeholder and the CTA disabled until the user chooses a product.
   */
  private setProductsForSelection(state: string, courseType: string | null, autoSelect: boolean): void {
    this.selectedProducts = [];
    this.data?.[0]?.productPickerTable?.forEach((item) => {
      if (item.state === state && (!courseType || item.courseType === courseType)) {
        this.selectedProducts.push({
          value: item.productName,
          label: item.productName,
          href: item.productLink,
          // Keep the raw per-product override; the single default-fallback decision
          // (ctaOverride -> block ctaLabel -> 'Get My License') happens in handleProductChange.
          ctaOverride: item.ctaOverride,
          noFollow: item.noFollow,
          noReferrer: item.noReferrer,
        });
      }
    });
    this.selectedProductsDisabled = this.selectedProducts.length === 0;
    // Auto-resolve when the standard picker asks for it (autoSelect) OR when the
    // finder selection yields a single destination — Continuing Education routes to
    // one comparison page, so state + course type is enough to enable the CTA with no
    // product pick (EPS-4593 AC1). Data-driven, so it also covers a single-product
    // Pre-License state and degrades gracefully if a course type has multiple rows.
    if ((autoSelect || this.selectedProducts.length === 1) && this.selectedProducts.length > 0) {
      // Select the first (or only) product and resolve the CTA. productDefault must be
      // set so the Product <select> shows that product — now that vision-select
      // reflects the model onto the native value, an empty productDefault would force
      // the placeholder instead (a regression from master, which relied on the browser
      // auto-picking the first option).
      this.productDefault = this.selectedProducts[0].value;
      this.handleProductChange(this.selectedProducts[0].value);
    } else {
      // INS finder: keep the Product select on its placeholder and the CTA cleared
      // until the user picks a product.
      this.productDefault = '';
      this.finalCtaButtonData = { ...this.finalCtaButtonData, buttonLink: '' };
    }
  }

  /**
   * Handles the change event of the product picker.
   * Updates the finalCtaButtonData based on the selected product.
   *
   * @param value - The value of the selected product.
   */
  handleProductChange(value: string) {
    this.selectedProducts.filter((product) => {
      if (product.value === value) {
        this.finalCtaButtonData = {
          ...this.finalCtaButtonData,
          buttonLink: product.href,
          nofollow: product.noFollow,
          noreferrer: product.noReferrer,
          buttonText: product.ctaOverride || this.blockDefaultLabel,
        };
      }
    });
  }
}
